import { css } from '@emotion/react';
import { MDXProvider } from '@mdx-js/react';
import { Box, List, Stack, styled } from '@mui/joy';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { createElement, useEffect, useRef } from 'react';

import { BackendContainer } from '@/components/backend/BackendContainer';
import type { HelpPageDirectory } from '@/components/content/HelpDirectoryList';
import { HelpDirectoryList } from '@/components/content/HelpDirectoryList';
import { MdxH1 } from '@/components/mdx/MdxH1';
import { MdxH2 } from '@/components/mdx/MdxH2';
import { MdxH3 } from '@/components/mdx/MdxH3';
import { MdxH4 } from '@/components/mdx/MdxH4';
import { MdxLink } from '@/components/mdx/MdxLink';
import { MdxOl } from '@/components/mdx/MdxOl';
import { MdxP } from '@/components/mdx/MdxP';
import { MdxTable } from '@/components/mdx/MdxTable';
import { MdxUl } from '@/components/mdx/MdxUl';
import { makeMetaTitle } from '@/modules/META';
import type { HelpPage } from 'content-collections';
import { allHelpPages } from 'content-collections';

declare global {
  interface Window {
    PagefindComponents?: {
      // eslint-disable-next-line @typescript-eslint/method-signature-style
      getInstanceManager(): {
        // eslint-disable-next-line @typescript-eslint/method-signature-style
        removeInstance(name: string): void;
      };
    };
  }
}

const SidebarBox = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    border-right: 2px solid ${theme.palette.divider};
    background-color: ${theme.vars.palette.background.level1};
    width: 20vw;
    max-width: ${theme.spacing(30)};
    min-height: 0;
    overflow-y: auto;
  `,
);

const StyledList = styled(List)(
  ({ theme }) => css`
    padding-top: ${theme.spacing(0)};
    padding-bottom: ${theme.spacing(4)};
    --List-nestedInsetStart: ${theme.spacing(3)};
  `,
);

const MdxStyles = styled(Box)(
  ({ theme }) => css`
    display: flex;
    flex: 1;

    img {
      margin-bottom: ${theme.spacing(2)};
    }

    .MuiAlert-root {
      margin-bottom: ${theme.spacing(2)};
    }
  `,
);

function humanize(slug: string): string {
  return slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const fetchHelpPageDirectories = createServerFn({ method: 'GET' }).handler(async () => {
  const directories = new Map<string, HelpPageDirectory>();

  return allHelpPages.reduce<(HelpPage | HelpPageDirectory)[]>((acc, item) => {
    const segments = item.path.split('/').filter(Boolean);

    if (segments.length === 2 && segments[0]) {
      const directory = segments[0];

      if (!directories.has(directory)) {
        const group: HelpPageDirectory = {
          directory,
          title: humanize(directory),
          items: [],
        };
        directories.set(directory, group);
        acc.push(group);
      }

      directories.get(directory)?.items.push(item);
    } else {
      acc.push(item);
    }

    return acc;
  }, []);
});

export const Route = createFileRoute('/verwaltung/hilfe')({
  loader: async () => fetchHelpPageDirectories(),
  head: () => ({
    meta: [makeMetaTitle(['Hilfe'])],
  }),
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const helpPageDirectories = Route.useLoaderData();
  const location = useLocation();
  const navigate = Route.useNavigate();

  useEffect(() => {
    if (!document.getElementById('pagefind-ui-css')) {
      const link = document.createElement('link');
      link.id = 'pagefind-ui-css';
      link.rel = 'stylesheet';
      link.href = '/pagefind/pagefind-component-ui.css';
      document.head.appendChild(link);
    }

    if (!customElements.get('pagefind-modal') && !document.getElementById('pagefind-ui-js')) {
      const script = document.createElement('script');
      script.id = 'pagefind-ui-js';
      script.type = 'module';
      script.src = '/pagefind/pagefind-component-ui.js';
      document.head.appendChild(script);
    }

    return () => {
      // Pagefind retains disconnected elements in its default instance; rebuild it on the next visit.
      window.PagefindComponents?.getInstanceManager().removeInstance('default');
    };
  }, []);

  useEffect(() => {
    const handlePagefindNavigation = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }

      const { target } = event;

      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('pagefind-modal a[href], pagefind-searchbox a[href]');

      if (!(anchor instanceof HTMLAnchorElement) || anchor.target === '_blank') {
        return;
      }

      const href = anchor.getAttribute('href');

      if (!href || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      const url = new URL(href, window.location.origin);

      if (url.origin !== window.location.origin) {
        return;
      }

      event.preventDefault();

      const modal = document.querySelector('pagefind-modal');

      if (modal && 'close' in modal) {
        const maybeClose = (modal as { close?: unknown }).close;

        if (typeof maybeClose === 'function') {
          maybeClose.call(modal);
        }
      }

      const helpPrefix = '/verwaltung/hilfe/';
      const splatPath = url.pathname.startsWith(helpPrefix) ? url.pathname.slice(helpPrefix.length) : url.pathname;

      navigate({
        to: '/verwaltung/hilfe/$',
        params: { _splat: splatPath },
        hashScrollIntoView: true,
        hash: url.hash.replace('#', ''),
        replace: false,
      });
    };

    document.addEventListener('click', handlePagefindNavigation);

    return () => {
      document.removeEventListener('click', handlePagefindNavigation);
    };
  }, [navigate]);

  const scrollTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = undefined;

    const hashId = location.hash ? decodeURIComponent(location.hash.replace(/^#/, '')) : '';

    if (!hashId) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 80;

    const scrollToHash = () => {
      const target = document.getElementById(hashId);

      if (target) {
        target.scrollIntoView({ block: 'start' });
      } else {
        attempts += 1;

        if (attempts < maxAttempts) {
          scrollTimerRef.current = window.setTimeout(scrollToHash, 50);
        }
      }
    };

    scrollToHash();
  }, [location.hash, location.pathname]);

  useEffect(
    () => () => {
      clearTimeout(scrollTimerRef.current);
    },
    [],
  );

  return (
    <Stack direction="row" sx={{ backgroundColor: 'common.white', flex: 1, minHeight: 0 }}>
      <SidebarBox>
        {createElement('pagefind-config', { 'bundle-path': '/pagefind/', lang: 'de' })}
        {/* Pagefind's trigger exposes `placeholder` as getter-only, so React must not receive it as a prop. */}
        {createElement('pagefind-modal-trigger', { style: { padding: '10px' }, lang: 'de' })}
        {createElement('pagefind-modal', {
          style: {
            '--pf-modal-top': '8dvh',
            '--pf-modal-max-width': '640px',
            '--pf-background': '#ffffff',
            '--pf-text': '#1a1a1a',
          },
        })}

        <StyledList>
          {helpPageDirectories.map((item) => (
            <HelpDirectoryList key={item.title} item={item} />
          ))}
        </StyledList>
      </SidebarBox>

      <MDXProvider
        components={{
          h1: MdxH1,
          h2: MdxH2,
          h3: MdxH3,
          h4: MdxH4,
          p: MdxP,
          ul: MdxUl,
          ol: MdxOl,
          table: MdxTable,
          a: MdxLink,
        }}
      >
        <MdxStyles>
          <BackendContainer maxWidth="md">
            <Outlet />
          </BackendContainer>
        </MdxStyles>
      </MDXProvider>
    </Stack>
  );
}
